import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChartLineUp, Lock, CaretRight, Prohibit } from "@phosphor-icons/react";

const fmtPrice = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");

export default function PlacementInsights() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    setLoading(true);
    const q = category && category !== "all" ? `?category=${encodeURIComponent(category)}` : "";
    api.get(`/placement-insights${q}`)
      .then(({ data }) => setData(data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); else toast.error("Couldn't load insights"); })
      .finally(() => setLoading(false));
  }, [category]);

  if (denied) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6" data-testid="insights-denied">
        <div className="text-center max-w-md">
          <Lock size={40} weight="fill" className="text-[#8A867D] mx-auto mb-4" />
          <h1 className="font-serif-display text-3xl font-bold mb-2">Team access only</h1>
          <p className="text-[15px] text-[#525252] mb-6">Placement insights are limited to the Vintage Vantage team.</p>
          <button onClick={() => navigate("/dashboard")} className="h-12 px-6 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium">Back to dashboard</button>
        </div>
      </div>
    );
  }

  const rows = data?.platforms || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="insights-page">
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/portfolio")} data-testid="insights-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
            <ArrowLeft size={18} weight="bold" /> Portfolio
          </button>
          <span className="text-sm font-semibold text-[#525252] truncate max-w-[200px]">{user?.name || user?.email}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-2"><ChartLineUp size={22} weight="fill" className="text-[#B47A3C]" /> <span className="label-caps">Placement signal · compounding data</span></div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">Where pieces get in — and get turned away</h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-8">
          Every time a seller reports a marketplace turned a piece down, we bank it here with the piece's details.
          Paired with what's actually listed, it builds the honest "acceptance rate by marketplace" that will power the
          best-fit recommender — <strong>the more that's logged, the sharper it gets.</strong>
        </p>

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="insights-category-filter">
          <span className="label-caps">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="insights-category-select" className="h-10 border border-[#0D1B2A]/30 rounded-none px-2 text-sm focus:border-[#0D1B2A] outline-none">
            <option value="all">All pieces</option>
            {(data?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {category !== "all" && <span className="text-sm text-[#8A867D]">acceptance for {category.toLowerCase()} only</span>}
        </div>

        {loading ? (
          <div className="p-10 text-center text-[#8A867D]">Loading the signal…</div>
        ) : rows.length === 0 ? (
          <div className="border border-[#0D1B2A]/20 bg-white p-10 text-center text-[#525252]" data-testid="insights-empty">
            No placement data yet. As sellers use the "Rejected?" flow, turn-downs appear here by marketplace.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8" data-testid="insights-summary">
              <div className="border border-[#0D1B2A]/20 bg-white p-6">
                <Prohibit size={22} weight="fill" className="text-[#B47A3C] mb-3" />
                <p className="font-serif-display text-3xl font-bold leading-none" data-testid="insights-total-rejected">{data.total_rejected}</p>
                <p className="label-caps mt-2">Turn-downs logged</p>
              </div>
              <div className="border border-[#0D1B2A]/20 bg-white p-6">
                <ChartLineUp size={22} weight="fill" className="text-[#0D1B2A] mb-3" />
                <p className="font-serif-display text-3xl font-bold leading-none">{rows.length}</p>
                <p className="label-caps mt-2">Marketplaces tracked</p>
              </div>
            </div>

            <div className="border border-[#0D1B2A]/20 bg-white overflow-hidden" data-testid="insights-table">
              <div className="overflow-x-auto">
                <table className="w-full text-[15px]">
                  <thead>
                    <tr className="text-left border-b border-[#F0EFEA] bg-[#F5F3ED]">
                      <th className="px-4 py-3 label-caps font-semibold">Marketplace</th>
                      <th className="px-4 py-3 label-caps font-semibold text-right">Submitted</th>
                      <th className="px-4 py-3 label-caps font-semibold text-right">Listed</th>
                      <th className="px-4 py-3 label-caps font-semibold text-right">Turned away</th>
                      <th className="px-4 py-3 label-caps font-semibold">Acceptance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <Fragment key={r.platform}>
                        <tr data-testid={`insights-row-${i}`} className={`border-b border-[#F7F6F1] ${i % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}>
                          <td className="px-4 py-3 font-medium text-[#0D1B2A]">
                            <button onClick={() => setExpanded(expanded === r.platform ? null : r.platform)} data-testid={`insights-expand-${i}`} className="inline-flex items-center gap-2 hover:text-[#B47A3C] transition-colors" disabled={!r.recent_rejected?.length}>
                              <CaretRight size={13} weight="bold" className={`transition-transform ${expanded === r.platform ? "rotate-90" : ""} ${r.recent_rejected?.length ? "" : "opacity-0"}`} />
                              {r.platform}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right text-[#525252]">{r.submitted}</td>
                          <td className="px-4 py-3 text-right text-[#047857] font-semibold">{r.active}</td>
                          <td className="px-4 py-3 text-right text-[#B47A3C] font-semibold">{r.rejected}</td>
                          <td className="px-4 py-3">
                            {r.acceptance_rate == null ? (
                              <span className="text-[#8A867D] text-sm">—</span>
                            ) : (
                              <div className="flex items-center gap-2 min-w-[120px]">
                                <div className="flex-1 h-2.5 bg-[#EDE9E0] overflow-hidden">
                                  <div className="h-full bg-[#0D1B2A]" style={{ width: `${Math.max(3, r.acceptance_rate)}%` }} />
                                </div>
                                <span className="text-sm font-semibold w-10 text-right">{r.acceptance_rate}%</span>
                              </div>
                            )}
                          </td>
                        </tr>
                        {expanded === r.platform && r.recent_rejected?.length > 0 && (
                          <tr className="bg-[#FBEDE7]/40" data-testid={`insights-rejected-${i}`}>
                            <td colSpan={5} className="px-6 py-4">
                              <p className="label-caps mb-2 text-[#B47A3C]">Recently turned away by {r.platform}</p>
                              <div className="space-y-1.5">
                                {r.recent_rejected.map((it, j) => (
                                  <div key={j} className="text-sm text-[#525252] flex flex-wrap items-center gap-x-2">
                                    <span className="font-medium text-[#0D1B2A]">{it.title || "Untitled piece"}</span>
                                    {it.period_era && <span>· {it.period_era}</span>}
                                    {it.condition && <span>· {it.condition}</span>}
                                    {(it.price_retail || it.price_retail === 0) && <span>· {fmtPrice(it.price_retail)}</span>}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-sm text-[#8A867D] mt-4 max-w-3xl">
              "Submitted" = listed + turned away. "Listed" is derived from pieces sellers track as live on each marketplace.
              We never sync marketplaces live — this is honest, self-reported signal that compounds over time.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
